<template>
  <div class="admin-shell">
    <div class="hidden lg:block fixed inset-y-0 left-0 z-40 print:hidden">
      <AdminSidebar :collapsed="sidebarCollapsed" @toggle-collapse="toggleSidebar" />
    </div>

    <AdminMobileHeader
      ref="mobileHeader"
      :title="pageTitle"
      :drawer-open="drawerOpen"
      class="print:hidden"
      @toggle-drawer="toggleDrawer"
    />

    <main
      class="admin-content min-w-0"
      :class="{ 'admin-content--sidebar-collapsed': sidebarCollapsed }"
    >
      <div v-if="loading && !stats" class="admin-page admin-page--centered">
        <i class="fa fa-spinner fa-spin fa-2x" aria-hidden="true"></i>
      </div>
      <router-view v-else />
    </main>

    <div v-if="canOpenChangelog" class="admin-utility-actions print:hidden">
      <button
        ref="changelogTrigger"
        type="button"
        class="admin-button--secondary"
        aria-haspopup="dialog"
        @click="openChangelog"
      >
        Versionsverlauf
      </button>
      <button
        type="button"
        class="admin-button--secondary"
        aria-label="Seite ohne Cache neu laden"
        title="Seite ohne Cache neu laden"
        @click="forceReload"
      >
        <i class="fa fa-rotate-right mr-2" aria-hidden="true"></i>
        Neu laden
      </button>
    </div>

    <AdminChangelogModal
      v-if="showChangelog"
      :version="changelog.version"
      :markdown="changelog.markdown"
      @dismiss="dismissChangelog"
    />

    <Transition name="admin-drawer-fade">
      <div
        v-if="drawerOpen"
        id="admin-mobile-drawer"
        class="admin-drawer print:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Administrationsnavigation"
        @keydown="handleDrawerKeydown"
      >
        <button
          type="button"
          class="admin-drawer__backdrop admin-button--ghost"
          aria-label="Navigation schließen"
          @click="closeDrawer"
        ></button>
        <div ref="drawerPanel" class="admin-drawer__panel" tabindex="-1">
          <AdminSidebar :collapsed="false" :collapsible="false" @navigate="closeDrawer" />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import AdminChangelogModal from "@/components/admin/AdminChangelogModal.vue";
import AdminMobileHeader from "@/components/admin/AdminMobileHeader.vue";
import AdminSidebar from "@/components/admin/AdminSidebar.vue";
import { useAdminSession } from "@/admin/adminSession";
import changelog from "@/generated/changelog.json";
import {
  dismissChangelog as persistChangelogDismissal,
  getChangelogDismissalKey,
  hasDismissedChangelog,
  isEligibleAdmin,
  shouldShowAdminChangelog,
} from "@/admin/changelogVisibility";
import "@/assets/admin.css";

const SIDEBAR_STORAGE_KEY = "casp.admin.sidebarCollapsed";

export default {
  name: "AdminLayout",
  components: { AdminChangelogModal, AdminMobileHeader, AdminSidebar },
  setup() {
    const route = useRoute();
    const { stats, loading, loadAdminSession } = useAdminSession();
    const sidebarCollapsed = ref(false);
    const drawerOpen = ref(false);
    const drawerPanel = ref(null);
    const mobileHeader = ref(null);
    const changelogDismissed = ref(false);
    const changelogOpen = ref(false);
    const changelogTrigger = ref(null);
    let previousBodyOverflow = "";

    const pageTitle = computed(() => route.meta.title || "");
    const canOpenChangelog = computed(
      () => route.name === "Admincenter" && isEligibleAdmin(stats.value)
    );
    const showChangelog = computed(
      () =>
        isEligibleAdmin(stats.value) &&
        (changelogOpen.value ||
          shouldShowAdminChangelog(stats.value, changelog.version, getLocalStorage()) &&
            !changelogDismissed.value)
    );

    function getLocalStorage() {
      try {
        return typeof window === "undefined" ? null : window.localStorage;
      } catch (_error) {
        return null;
      }
    }

    function refreshChangelogDismissal(statsValue) {
      if (!statsValue?.user?.id && statsValue?.user?.id !== 0) {
        changelogDismissed.value = false;
        return;
      }
      const key = getChangelogDismissalKey(changelog.version, statsValue.user.id);
      changelogDismissed.value =
        isEligibleAdmin(statsValue) &&
        hasDismissedChangelog(getLocalStorage(), key);
    }

    function dismissChangelog() {
      const currentStats = stats.value;
      if (currentStats?.user?.id === undefined || currentStats?.user?.id === null) {
        changelogDismissed.value = true;
        return;
      }
      persistChangelogDismissal(
        getLocalStorage(),
        getChangelogDismissalKey(changelog.version, currentStats.user.id)
      );
      changelogDismissed.value = true;
      changelogOpen.value = false;
      nextTick(() => changelogTrigger.value?.focus());
    }

    function openChangelog() {
      if (isEligibleAdmin(stats.value)) {
        changelogOpen.value = true;
      }
    }

    function forceReload() {
      const url = new URL(window.location.href);
      url.searchParams.set("_reload", Date.now().toString());
      window.location.replace(url.toString());
    }

    function toggleSidebar() {
      sidebarCollapsed.value = !sidebarCollapsed.value;
    }

    function closeDrawer() {
      drawerOpen.value = false;
    }

    function toggleDrawer() {
      drawerOpen.value = !drawerOpen.value;
    }

    function focusableElements(container) {
      return Array.from(
        container?.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      );
    }

    function handleDrawerKeydown(event) {
      if (event.key === "Escape") {
        closeDrawer();
        return;
      }
      if (event.key !== "Tab") return;

      const elements = focusableElements(event.currentTarget);
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onMounted(() => {
      try {
        sidebarCollapsed.value = getLocalStorage()?.getItem(SIDEBAR_STORAGE_KEY) === "true";
      } catch (_error) {
        sidebarCollapsed.value = false;
      }
      loadAdminSession();
    });

    watch(sidebarCollapsed, (collapsed) => {
      try {
        getLocalStorage()?.setItem(SIDEBAR_STORAGE_KEY, String(collapsed));
      } catch (_error) {
        // Browser storage can be unavailable in private browsing contexts.
      }
    });
    watch(
      stats,
      (statsValue) => {
        refreshChangelogDismissal(statsValue);
        changelogOpen.value = false;
      },
      { immediate: true }
    );

    watch(drawerOpen, async (open) => {
      if (open) {
        previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        await nextTick();
        drawerPanel.value?.focus();
      } else {
        document.body.style.overflow = previousBodyOverflow;
        await nextTick();
        mobileHeader.value?.$el.querySelector("button")?.focus();
      }
    });

    onBeforeUnmount(() => {
      document.body.style.overflow = previousBodyOverflow;
    });

    return {
      stats,
      loading,
      changelog,
      canOpenChangelog,
      showChangelog,
      changelogTrigger,
      pageTitle,
      sidebarCollapsed,
      drawerOpen,
      drawerPanel,
      mobileHeader,
      toggleSidebar,
      toggleDrawer,
      closeDrawer,
      handleDrawerKeydown,
      dismissChangelog,
      openChangelog,
      forceReload,
    };
  },
};
</script>

<style scoped>
.admin-drawer-fade-enter-active,
.admin-drawer-fade-leave-active {
  transition: opacity 150ms ease;
}

.admin-drawer-fade-enter-from,
.admin-drawer-fade-leave-to {
  opacity: 0;
}

.admin-drawer-fade-enter-active .admin-drawer__panel,
.admin-drawer-fade-leave-active .admin-drawer__panel {
  transition: transform 180ms ease;
}

.admin-drawer-fade-enter-from .admin-drawer__panel,
.admin-drawer-fade-leave-to .admin-drawer__panel {
  transform: translateX(-100%);
}
</style>
